import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import authStorage from "../utils/authStorage";
import { BASE_URL } from "../config";

const SLUG = "medicalConsent";
const BRAND = "#E53E14";

// Admin page to edit the medical-condition consent disclaimer shown on the
// public signup page. Content is stored in the DB (SiteContent collection).
const MedicalConsentManage = () => {
  const editorRef = useRef(null);
  const [title, setTitle] = useState("");
  const [initialContent, setInitialContent] = useState("");
  // The last values the server accepted. Cancel restores these, so it has to be
  // kept apart from the working copy rather than re-read from the editor.
  const [saved, setSaved] = useState({ title: "", content: "" });
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Restrict to admins; mirror the guard used by other admin pages.
    const isAdmin = ["admin"].includes(authStorage.getItem("roles"));
    if (!isAdmin) {
      window.location = window.location.origin + "/dashboard";
      return;
    }
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/content/${SLUG}`);
      if (res?.data?.data) {
        const t = res.data.data.title || "";
        const c = res.data.data.content || "";
        setTitle(t);
        setInitialContent(c);
        setSaved({ title: t, content: c });
      }
    } catch (err) {
      console.log("Failed to load content", err);
      toast.error("Failed to load content");
    } finally {
      setLoaded(true);
    }
  };

  const handleSave = async () => {
    const content = editorRef.current ? editorRef.current.getContent() : initialContent;
    if (!content || !content.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await axios({
        method: "PUT",
        url: `${BASE_URL}/content/${SLUG}`,
        headers: { "x-access-token": authStorage.getItem("auth-token") },
        data: { title, content },
      });
      setSaved({ title, content });
      setDirty(false);
      if (editorRef.current) editorRef.current.setDirty(false);
      toast.success("Consent content updated");
    } catch (err) {
      console.log("Failed to save content", err);
      toast.error(err?.response?.data?.message || "Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTitle(saved.title);
    // The editor is uncontrolled (`initialValue`), so React re-rendering will not
    // put the old text back -- it has to be pushed in.
    if (editorRef.current) {
      editorRef.current.setContent(saved.content);
      editorRef.current.setDirty(false);
    }
    setDirty(false);
  };

  return (
    <div className="dark:bg-slate-900 p-4 md:p-6 min-h-screen text-left">
      {/*
        `h2.mob-header` is display:none above 768px by design -- on desktop the
        page title is the one in the shell's top bar. This page previously used a
        bare <h1>, which the global stylesheet paints WHITE (`h1 { color: #fff }`),
        so the heading was invisible against the page. Never use a bare <h1> here.
      */}
      <h2 className="mob-header font-medium mb-5">Medical Consent</h2>

      {/* Every other page in this shell sits on a white card; without it the form
          floated directly on the grey page background and read as unstyled. */}
      <div className="bg-white rounded-lg p-4 md:p-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-900">Medical Consent Notice</h3>
          <p className="mt-1 text-sm text-gray-500">
            This content appears in the &ldquo;Read more&rdquo; popup next to the
            medical-condition consent checkbox on the signup page. It is shown to
            everyone signing up, so keep the wording legally exact.
          </p>
        </div>

        <div className="mb-5">
          <label htmlFor="consent-title" className="block pb-2 text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            id="consent-title"
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            placeholder="Health &amp; Medical Disclaimer"
            className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm placeholder:text-gray-400 focus:border-[#415E96] focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-500">The popup's heading.</p>
        </div>

        <label className="block pb-2 text-sm font-medium text-gray-700">Content</label>
        {loaded ? (
          <Editor
            apiKey="g64cjnqhg5z2xyt5dqqnvtvugubbr19suf3mljwa9h2ys1sr"
            onInit={(evt, editor) => (editorRef.current = editor)}
            initialValue={initialContent}
            onDirty={() => setDirty(true)}
            init={{
              height: 350,
              menubar: true,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "preview",
                "anchor",
                "searchreplace",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "undo redo | blocks | bold italic forecolor | " +
                "alignleft aligncenter alignright alignjustify | " +
                "bullist numlist outdent indent | removeformat | help",
              content_style:
                "body { font-size:14px; background-color: #fff; color: rgba(0, 0, 0, 0.7); }",
            }}
          />
        ) : (
          // The editor mounts only once the content has arrived, so without this
          // the page showed a bare label over empty space while it loaded.
          <Skeleton height={350} />
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-0 border-t border-solid border-gray-200 pt-5">
          {/* NOTE: this project loads only `@tailwind utilities` (see src/index.css)
              -- there is NO preflight, so a bare <button> keeps Chrome's default
              outset border and grey button face. `border-0 appearance-none` is
              spelled out here for that reason; Cancel needs its border restored
              explicitly for the same one. */}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            type="button"
            className="cursor-pointer rounded-lg border-0 px-10 py-2.5 text-center text-sm font-medium text-white appearance-none disabled:cursor-not-allowed disabled:opacity-50"
            style={{ backgroundColor: BRAND }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving || !dirty}
            type="button"
            className="cursor-pointer rounded-lg border border-solid border-gray-300 bg-white px-8 py-2.5 text-center text-sm font-medium text-gray-700 appearance-none hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          {dirty && (
            <span className="text-sm" style={{ color: "#b45309" }}>
              Unsaved changes
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalConsentManage;
